import { HideRegistrations, RegistrantResponse } from '@/connection/audiences/registrants';
import Table from '@layouts/layout-table/layout-table';
import TableBody from '@layouts/layout-table/layout-table-body';
import OptionalComponent from '@ui/optional-component/optional-component';
import RegistrantsTableHeader from './registrants-table-header';
import RegistrantsTableRow from './registrants-table-row';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectMemberPasscodeLists, selectPasscodeLists } from '@/store/slices/passcode-lists/selectors';
import Text from '@/general-ui/text/text/text';
import {
  getWorkingProject,
  getWorkingProjectUuid,
  selectIsCourseEnabled,
  selectProjectChannel,
  selectWorkingProjectMagicLinkRegistrationsPasscodeListId,
} from '@/store/slices/project/selectors';
import { Sort } from '../../types';
import { selectProjectCourse, selectRegistrants, selectRegistrantsCount } from '@/store/slices/registrants/selectors';
import Paging from '@ui/paging/paging';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { REGISTRANT_LIST_PAGE_SIZE } from '@/types/registration.types';
import AudiencesTableSelectedMemberActions from '../audiences-table-selected-member-actions';
import Container from '@ui/container/container';
import { setPersonCourseCompletionState } from '@/store/slices/registrants';
import { CheckCertificateGenerating, ForceCompleteCourse, GetCourseCertDownloadForUser } from '@/connection/courses';
import toastTrigger from '@ui/toast/toast-trigger';
import { useProjectCourse } from '@/micros/edit-project/grid-areas/main/audiences/hooks/use-project-course';
import { useLoadRegistrants } from '@/micros/edit-project/grid-areas/main/audiences/hooks/use-load-registrants';
import { canEdit } from '@/micros/edit-project/grid-areas/main/audiences/default-passcode-lists';

type RegistrantsTableProps = {
  sort: Sort | undefined;
  setSort: (sort: Sort | undefined) => void;
  status?: string;
};
const RegistrantsTable = ({ sort, setSort, status }: RegistrantsTableProps) => {
  const dispatch = useAppDispatch();
  const registrants = useAppSelector(selectRegistrants);
  const registrantsCount = useAppSelector(selectRegistrantsCount);
  const memberPasscodeLists = useAppSelector(selectMemberPasscodeLists);
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const project = useAppSelector(getWorkingProject);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const magicLinkRegistrantsPasscodeListId = useAppSelector(selectWorkingProjectMagicLinkRegistrationsPasscodeListId);
  const coursesEnabled = useAppSelector(selectIsCourseEnabled);
  const channel = useAppSelector(selectProjectChannel);
  const course = useAppSelector(selectProjectCourse);

  const mounted = useRef(true);

  const [params, setParams] = useSearchParams();
  const paramsObj = Object.fromEntries(params.entries());
  const hasFiltersApplied = !!(paramsObj.search || paramsObj.lists || paramsObj.status);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [generatingPdfBlProfiles, setGeneratingPdfBlProfiles] = useState<number[]>([]);
  const [updatingCourseCompletion, setUpdatingCourseCompletion] = useState<number[]>([]);

  useProjectCourse(channel || 0, projectUuid || '', coursesEnabled || false);

  const { loadRegistrants: reloadRegistrants } = useLoadRegistrants(
    projectUuid || '',
    REGISTRANT_LIST_PAGE_SIZE,
    magicLinkRegistrantsPasscodeListId || 0,
    true,
    coursesEnabled,
    channel
  );

  const downloadPdf = useCallback(
    async (blProfile: number) => {
      try {
        if (!course || !channel) {
          return;
        }

        const url = await GetCourseCertDownloadForUser(channel, course.uuid, blProfile);
        if (!url?.url) {
          return;
        }

        const link = document.createElement('a');
        link.href = url.url;
        link.setAttribute('download', `certificate.pdf`);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        console.error(e);
        toastTrigger(
          <div className="S1">
            <div>Error getting certificate link</div>
            <div>We ran into an error trying to get the certificate link.</div>
          </div>,
          {
            type: 'error',
            autoClose: 10000,
          }
        );
      }
    },
    [course, channel]
  );

  const forceCourseCompletion = useCallback(
    async (blProfile: number) => {
      if (!course || !project?.channel) {
        return;
      }

      try {
        setUpdatingCourseCompletion((prevState) => [...prevState, blProfile]);

        const completed = await ForceCompleteCourse(project?.channel, course.uuid, blProfile);

        if (completed.certificateLocation) {
          // TODO: make this automatically update when pdf is generated
          // as of right now user must navigate away and back to see if this pdf is ready or not
          // and for right now I'm calling that acceptible clunkiness
          setGeneratingPdfBlProfiles((prevState) => [...prevState, blProfile]);
          dispatch(
            setPersonCourseCompletionState({
              bl_profile: blProfile,
              state: 'pdf-generating',
              certificateLocation: completed.certificateLocation,
            })
          );
        } else {
          dispatch(
            setPersonCourseCompletionState({
              bl_profile: blProfile,
              state: 'completed-no-pdf',
              certificateLocation: null,
            })
          );
        }
      } catch (e) {
        console.error(e);
        toastTrigger(
          <div className="S1">
            <div>Error updating course completion</div>
            <div>We ran into an error trying to update the course for that user.</div>
          </div>,
          {
            type: 'error',
            autoClose: 10000,
          }
        );
      } finally {
        setUpdatingCourseCompletion((prevState) => prevState.filter((id) => id !== blProfile));
      }
    },
    [course, project?.channel, dispatch]
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    async function checkProfiles() {
      try {
        if (!course || !channel) {
          return;
        }

        const profilesComplete: number[] = [];

        for (const profile of generatingPdfBlProfiles) {
          const state = await CheckCertificateGenerating(channel, course.uuid, profile);
          if (state.generating) {
            continue;
          }

          dispatch(
            setPersonCourseCompletionState({ bl_profile: profile, state: 'pdf-ready', certificateLocation: null })
          );
          profilesComplete.push(profile);
        }

        // do not set local state if the user has navigated away, just stop
        if (!mounted.current) {
          return;
        }

        if (profilesComplete.length) {
          setGeneratingPdfBlProfiles((prevState) => prevState.filter((id) => !profilesComplete.includes(id)));
        }

        timeout = setTimeout(checkProfiles, 5000);
      } catch (e) {
        /// not alerting since this isn't a transactional thing
        console.error(e);
      }
    }

    timeout = setTimeout(checkProfiles, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [generatingPdfBlProfiles, course, channel, dispatch]);

  const getRegistrantPasscodeLists = useCallback(
    (email: string): string[] => {
      const memberListIds = memberPasscodeLists[email];
      let passcodeListNames = passcodeLists
        ?.map((passcodeList) => {
          if (passcodeList.passcode_list && memberListIds?.includes(passcodeList.passcode_list)) {
            return passcodeList.name;
          }
          return;
        })
        .filter((name) => name !== undefined); // Filter out undefined values

      // sort by length
      if (passcodeListNames.length > 1) {
        passcodeListNames = passcodeListNames?.sort((a, b) => a.length - b.length);
      }

      return passcodeListNames || [];
    },
    [memberPasscodeLists, passcodeLists]
  );

  const handleIsSelected = useCallback(
    (registrantUuid: string) => {
      if (selectedMembers.includes(registrantUuid)) {
        setSelectedMembers(selectedMembers.filter((uuid) => uuid !== registrantUuid));
      } else {
        setSelectedMembers([...selectedMembers, registrantUuid]);
      }
    },
    [selectedMembers]
  );

  const handleSelectAll = () => {
    const trueRegistrantUuids = registrants
      .filter((registrant) => registrant.uuid !== null)
      .map((registrant) => registrant.uuid);
    if (trueRegistrantUuids.every((registrationUuid) => selectedMembers.includes(registrationUuid))) {
      //all members are already selected, so deselect all
      setSelectedMembers([]);
    } else {
      //select all members
      setSelectedMembers(trueRegistrantUuids);
    }
  };

  const handleHideRegistrants = async () => {
    if (projectUuid && selectedMembers.length) {
      try {
        await HideRegistrations(selectedMembers, projectUuid);
      } catch (e) {
        console.error('Failed to hide registrants: ', e);
        toastTrigger(
          <div className="S1">
            <div>Error hiding registrants</div>
            <div>Please refresh the page and try again.</div>
          </div>,
          {
            type: 'error',
            autoClose: 10000,
          }
        );
      }

      reloadRegistrants();
    }
  };

  const isAllRegistrantsSelected = () => {
    const registered = registrants.map((registrant) => registrant.uuid).filter((uuid) => uuid !== null); // list is combined registrants and upload registrants without a registrant record. Filter out null UUIDs

    return registered.length > 0 && registered.every((registrantUuid) => selectedMembers.includes(registrantUuid));
  };

  const hasNonDeletedSelected = () => {
    return registrants.some((registrant) => selectedMembers.includes(registrant.uuid) && !registrant.deleted);
  };

  const getTableContentsHeight = () => {
    let heightToRemove = 308; //height in px of the app header and the registrant header
    const totalPages = Math.ceil(Math.ceil(registrantsCount / REGISTRANT_LIST_PAGE_SIZE));

    //pagenation buttons will be showing
    if (totalPages > 1) {
      heightToRemove += 32; // height of the paging component
    }

    return `calc(100vh - ${heightToRemove}px)`;
  };

  const allRegistrants = useMemo(() => {
    if (registrants) {
      return registrants.map((registrant: RegistrantResponse, index) => {
        return (
          <RegistrantsTableRow
            key={registrant.uuid + index || registrant.email + index}
            registrant={registrant}
            downloadCertificate={downloadPdf}
            forceCourseCompletion={forceCourseCompletion}
            updatingUsers={updatingCourseCompletion}
            generatingPdfForUsers={generatingPdfBlProfiles}
            memberOfPasscodeLists={getRegistrantPasscodeLists(registrant.email)}
            setIsSelected={() => handleIsSelected(registrant.uuid)}
            isSelected={selectedMembers.includes(registrant.uuid)}
            reloadRegistrants={reloadRegistrants}
            canEdit={canEdit(status || '')}
          />
        );
      });
    } else {
      return [];
    }
  }, [
    downloadPdf,
    forceCourseCompletion,
    generatingPdfBlProfiles,
    getRegistrantPasscodeLists,
    handleIsSelected,
    registrants,
    reloadRegistrants,
    selectedMembers,
    status,
    updatingCourseCompletion,
  ]);

  return (
    <>
      <div
        style={{
          visibility: selectedMembers.length > 0 ? 'visible' : 'hidden',
          maxHeight: selectedMembers.length > 0 ? '100px' : 0,
          overflow: 'hidden',
          transition: 'max-height 500ms ease-in-out',
        }}
      >
        <AudiencesTableSelectedMemberActions
          selectedMembers={selectedMembers}
          selectedRegistrants={registrants.filter((r) => selectedMembers.includes(r.uuid))}
          isRegistration
          isAllSelected={isAllRegistrantsSelected()}
          selectAll={handleSelectAll}
          clearSelectedMembers={() => setSelectedMembers([])}
          hideRegistrants={handleHideRegistrants}
          hasNonDeletedSelected={hasNonDeletedSelected()}
          onReloadRegistrants={reloadRegistrants}
        />
      </div>
      <Container className="margin-b-16" height={getTableContentsHeight()} style={{ overflowY: 'auto' }}>
        <Table key={registrants.length}>
          <RegistrantsTableHeader
            sort={sort}
            setSort={setSort}
            isAllSelected={isAllRegistrantsSelected()}
            selectAll={handleSelectAll}
            canEdit={canEdit(status || '')}
          />
          <TableBody>{allRegistrants}</TableBody>
        </Table>
      </Container>
      <OptionalComponent display={registrants.length === 0 && hasFiltersApplied}>
        <div className="flex-column jc-center ai-center padding-t-100">
          <Text tag="h2">No matches found for your search</Text>
        </div>
      </OptionalComponent>
      <OptionalComponent display={registrantsCount > REGISTRANT_LIST_PAGE_SIZE}>
        <Paging
          totalPages={Math.ceil(registrantsCount / REGISTRANT_LIST_PAGE_SIZE)}
          page={paramsObj.page ? parseInt(paramsObj.page) : 1}
          setPage={(num) => {
            setParams((prev) => {
              const currParams = Object.fromEntries(prev.entries());
              return {
                ...currParams,
                page: num.toString(),
              };
            });
          }}
        />
      </OptionalComponent>
    </>
  );
};

export default RegistrantsTable;
